/* global updateClientGroupCategoryUrl deleteClientGroupCategoryUrl storeClientGroupSubcategoryUrl */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Modal } from 'flowbite';

import ClientGroupNumberSubcategoryRow from './ClientGroupNumberSubcategoryRow';
import ClientGroupSourceTag from './ClientGroupSourceTag';
import ConfirmAddSubcategoryModal from './ConfirmAddSubcategoryModal';

import { formatFigure } from 'Helpers/formatService';
import { modalOptions } from 'Helpers/modalOptions';

import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import TextButton from 'CommonComponents/TextButton';

import useModal from 'Hooks/useModal';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import CirclePlusIcon from 'Assets/icons/circle-plus.svg';
import EditIcon from 'Assets/icons/edit.svg';
import TrashIcon from 'Assets/icons/trash.svg';

export default function ClientGroupNumberCategoryRow(props) {
    const [toggle, setToggle] = useState(false);
    const [editing, setEditing] = useState(props.category.isNew ?? false);

    // Sync toggle state when parent broadcasts collapse/expand
    useEffect(() => {
        if (props.expandKey > 0) {
            setToggle(props.forceExpanded);
        }
    }, [props.expandKey, props.forceExpanded]);
    const [deleted, setDeleted] = useState(false);
    const [subcategories, setSubcategories] = useState([]);
    const [categoryTotal, setCategoryTotal] = useState(0);
    const [name, setName] = useState(props.category.client_group_custom_name == null ? props.category.name : props.category.client_group_custom_name.name);

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const [categoryDeleteModalElement, setCategoryDeleteModalElement] = useState(null);
    const categoryDeleteModalRef = useRef();

    const confirmAddSubcategoryModal = useModal();

    const cleanCategoryDeleteModalStates = useCallback(() => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    }, [actionInProgress, actionFailed, actionCompleted]);

    const hideModalElement = (modalElement) => {
        modalElement && modalElement.hide();
    };

    const successHandlerDelete = () => {
        hideModalElement(categoryDeleteModalElement);
        setDeleted(true);
    };

    const triggerDelete = (category_id) => {
        setActionInProgress(true);
        deleteCategory(category_id);
    };

    const closeModal = () => {
        hideModalElement(categoryDeleteModalElement);
    };

    useEffect(() => {
        setSubcategories(props.category.client_group_subcategories);
        calculateCategoryTotal();
    }, []);

    const toggleEditing = () => {
        props.setError('');
        setEditing(!editing);
    };

    const handleChangeName = (event) => {
        setName(event.target.value);
    };

    const allowsCreatingSubcategories = () => {
        return !(
            ['teams', 'teams(_athletes_and_teams_support)', 'teams_athletes_and_teams_support', 'technical_officials', 'spectators'].includes(
                props.category.slug,
            ) && !props.category.event_id
        );
    };

    const allowsCreatingSecondSubcategories = () => {
        return !(
            ['teams', 'teams(_athletes_and_teams_support)', 'teams_athletes_and_teams_support', 'technical_officials', 'spectators', 'workforce'].includes(
                props.category.slug,
            ) && !props.category.event_id
        );
    };

    const saveEditing = (category_id) => {
        props.setError('');
        props.category.isNew = false;

        axios
            .put(updateClientGroupCategoryUrl.replace('CLIENT_GROUP_CATEGORY_ID', category_id), {
                name: name,
            })
            .then((res) => {
                if (res.data.clientGroupCategory.client_group_custom_name != null) {
                    props.category.client_group_custom_name = res.data.clientGroupCategory.client_group_custom_name;
                } else {
                    props.category.name = res.data.clientGroupCategory.name;
                }

                setEditing(false);
            })
            .catch((error) => {
                props.setError('There was an error editing the Category');
            });
    };

    const deleteCategory = (category_id) => {
        props.setError('');

        axios
            .delete(deleteClientGroupCategoryUrl.replace('CLIENT_GROUP_CATEGORY_ID', category_id))
            .then((res) => {
                setActionCompleted(true);
            })
            .catch((error) => {
                setActionFailed(true);
                props.setError('There was an error deleting the Category');
            });
    };

    const storeNewSubcategory = () => {
        props.setError('');
        confirmAddSubcategoryModal.hide();

        axios
            .post(storeClientGroupSubcategoryUrl.replace('CLIENT_GROUP_CATEGORY_ID', props.category.id), {
                name: 'New Subcategory',
            })
            .then((res) => {
                const updatedSubCategories = [
                    ...subcategories,
                    {
                        id: res.data.clientGroupSubcategory.id,
                        name: res.data.clientGroupSubcategory.name,
                        slug: res.data.clientGroupSubcategory.slug,
                        value: res.data.clientGroupSubcategory.value,
                        event_id: res.data.clientGroupSubcategory.event_id,
                        client_group_category_id: res.data.clientGroupSubcategory.client_group_category_id,
                        client_group_second_sub_categories: [],
                        isNew: true,
                    },
                ];
                setSubcategories(updatedSubCategories);
                props.category.client_group_subcategories = updatedSubCategories;
                setToggle(true);
            })
            .catch((error) => {
                props.setError(error.response.data.error ?? 'There was an error creating a New Subcategory');
            });
    };

    const calculateCategoryTotal = () => {
        const thisSubcategories = subcategories.length > 0 ? subcategories : props.category.client_group_subcategories;
        if (thisSubcategories === null) return;
        setCategoryTotal(
            thisSubcategories.reduce((categoryTotal, subcategory) => {
                return categoryTotal + (!subcategory.event_id ? Number(props.staticData[subcategory.slug]) : Number(subcategory.value));
            }, 0),
        );
        setSubcategories(thisSubcategories.filter((subcategory) => !subcategory.isDeleted));
    };

    const hasSubcategories = () => {
        if (subcategories === null) return;
        return subcategories.filter((subcategory) => !subcategory.isDeleted).length > 0;
    };

    useEffect(() => {
        if (!deleted) {
            setCategoryDeleteModalElement(new Modal(categoryDeleteModalRef.current, modalOptions(false)));
        }
    }, [deleted]);

    const showDeleteCategory = () => {
        categoryDeleteModalElement && categoryDeleteModalElement.show();
    };

    const categorySourceTag = props.sourceTags ? props.sourceTags[`category_${props.category.id}`] : null;
    const categoryDisplayName = props.category.client_group_custom_name == null ? props.category.name : props.category.client_group_custom_name.name;

    return !deleted ? (
        <>
            <tr key={props.category.id}>
                {!editing && !hasSubcategories() && (
                    <td className="tw-font-semibold">
                        <div className="tw-flex tw-items-center tw-gap-x-2 tw-flex-wrap">
                            <span>{categoryDisplayName}</span>
                            {categorySourceTag && <ClientGroupSourceTag source={categorySourceTag} />}
                        </div>
                    </td>
                )}
                {!editing && hasSubcategories() && (
                    <td
                        onClick={() => {
                            setToggle(!toggle);
                        }}
                    >
                        <div className="tw-flex tw-items-center tw-gap-x-1.5 tw-font-semibold tw-cursor-pointer tw-flex-wrap">
                            <div className="tw-flex tw-items-center tw-gap-x-1.5">
                                <span>{categoryDisplayName}</span>
                                <span>{toggle ? <ChevronUpIcon /> : <ChevronDownIcon />}</span>
                            </div>
                            {categorySourceTag && <ClientGroupSourceTag source={categorySourceTag} />}
                        </div>
                    </td>
                )}
                {editing && (
                    <td>
                        <input
                            type="text"
                            step="1"
                            name={`client_group_subcategory_${props.category.id}`}
                            className="tw-w-full tw-rounded-lg tw-bg-input-background tw-text-input-text tw-font-semibold tw-border-0 tw-py-1"
                            id={`client_group_subcategory_${props.category.id}`}
                            value={name}
                            onChange={(event) => handleChangeName(event)}
                        />
                    </td>
                )}
                <td className="inbud-numeric-cell tw-font-semibold">
                    {!(props.category.slug === 'spectators' && !props.category.event_id) && formatFigure(categoryTotal)}
                </td>
                <td>
                    <div className="tw-flex tw-justify-between">
                        <div className="tw-flex tw-items-center tw-gap-x-3">
                            {(props.category.event_id || ['media', 'v_i_ps'].includes(props.category.slug)) &&
                                props.canUpdate &&
                                (editing ? (
                                    <TextButton variant="primary" onClick={() => saveEditing(props.category.id)}>
                                        <CircleCheckIcon className="tw-w-3 tw-h-3" /> {$t('Save')}
                                    </TextButton>
                                ) : (
                                    <TextButton onClick={() => toggleEditing()}>
                                        <EditIcon className="tw-w-3 tw-h-3" />
                                        {$t('Edit')}
                                    </TextButton>
                                ))}
                            {allowsCreatingSubcategories() && props.canUpdate && props.canCreate && (
                                <TextButton
                                    variant="primary"
                                    onClick={() => (props.category.daily_demands_exists ? confirmAddSubcategoryModal.show() : storeNewSubcategory())}
                                >
                                    <CirclePlusIcon className="tw-w-3 tw-h-3" /> {$t('Add Subcategory')}
                                </TextButton>
                            )}
                            {(props.category.event_id || ['media', 'v_i_ps'].includes(props.category.slug)) && props.canDelete && (
                                <TextButton variant="danger" onClick={() => showDeleteCategory(props.category.id)}>
                                    <TrashIcon className="tw-w-3 tw-h-3" /> {$t('Delete')}
                                </TextButton>
                            )}
                        </div>
                    </div>
                    <ConfirmationModal
                        closeHandler={closeModal}
                        successHandler={successHandlerDelete}
                        triggerAction={() => triggerDelete(props.category.id)}
                        cleanStates={cleanCategoryDeleteModalStates}
                        allowAction={true}
                        modalId={`delete-category-${props.category.id}`}
                        modalRef={categoryDeleteModalRef}
                        modalElement={categoryDeleteModalElement}
                        request={actionInProgress}
                        actionCompleted={actionCompleted}
                        actionFailed={actionFailed}
                        confirmationMsg={$t('Please confirm you want to delete {{name}} as this action cannot be reversed', { name: props.category.name })}
                        actionCompletedMsg={$t('{{name}} deleted', { name: props.category.name })}
                        actionFailedMsg={$t('{{name}} could not be deleted', { name: props.category.name })}
                        title={$t('Delete Category')}
                        cancelButtonText="Cancel"
                        primaryButtonText="Delete"
                    />
                    <ConfirmAddSubcategoryModal modal={confirmAddSubcategoryModal} onConfirm={storeNewSubcategory} />
                </td>
            </tr>
            {toggle &&
                hasSubcategories() &&
                subcategories.map((subcategory) => {
                    return (
                        (props.category.slug !== 'workforce' || !!props.staticData[subcategory.slug] || !!subcategory.event_id) && (
                            <ClientGroupNumberSubcategoryRow
                                key={subcategory.id}
                                subcategory={subcategory}
                                staticData={props.staticData}
                                overrides={props.overrides}
                                sourceTags={props.sourceTags}
                                onOverrideChange={props.onOverrideChange}
                                calculateCategoryTotal={() => calculateCategoryTotal()}
                                allowsCreatingSecondSubcategories={allowsCreatingSecondSubcategories()}
                                setError={props.setError}
                                canCreate={props.canCreate}
                                canUpdate={props.canUpdate}
                                canDelete={props.canDelete}
                                forceExpanded={props.forceExpanded}
                                expandKey={props.expandKey}
                            />
                        )
                    );
                })}
        </>
    ) : (
        <></>
    );
}
